// @ts-nocheck

import { createSelector } from 'reselect';

import { selectUserConnected, selectUserDidReceivedUserData } from '../../../../selectors/user/userPrimary.selector';
import {
  KLBannerAnimatedBackgroundPathByBannerStates,
  KLBannerAnimatedBackgroundPathFallbackByBannerStates,
  KLBannerStaticBackgroundPathByBannerStates,
} from '../../constants/assets';
import { KLBannerStates } from '../../types/banner';
import { KLGlobalStatuses } from '../../types/global';
import { KLNotificationTypes } from '../../types/notification';
import { KLRounds } from '../../types/round';
import { KLUserStatuses } from '../../types/user';
import { selectKLGlobalStatus } from '../global';
import { selectKLUserLastMoveTs, selectKLUserNotifications, selectKLUserRound, selectKLUserStatus } from '../user';

const KLBannerSelectors = {};

const KLBannersInfo = {
  [KLBannerStates.TEASER]: {},
  [KLBannerStates.ENDING]: {},
  [KLBannerStates.START]: {
    buttonLabel: 'Commencer',
  },
  [KLBannerStates.ELIMINATED]: {
    text: 'Élimination',
    buttonLabel: 'Recommencer',
  },
  [KLBannerStates.T_RED]: {
    text: 'Tribu Rouge',
  },
  [KLBannerStates.T_WHITE]: {
    text: 'Tribu Blanche',
  },
  [KLBannerStates.ORIENTATION]: {
    text: 'Orientation',
  },
  [KLBannerStates.POLE]: {
    text: 'Poteaux',
  },
};

const getBannerStateForRound = (round) => {
  switch (round) {
    case KLRounds.T_RED:
      return KLBannerStates.T_RED;
    case KLRounds.T_WHITE:
      return KLBannerStates.T_WHITE;
    case KLRounds.ORIENTATION:
      return KLBannerStates.ORIENTATION;
    case KLRounds.POLE:
      return KLBannerStates.POLE;
    default:
      return KLBannerStates.TEASER;
  }
};

const getBannerStateForStatus = (status, round) => {
  switch (status) {
    case KLUserStatuses.IN:
      return getBannerStateForRound(round);
    case KLUserStatuses.OUT:
      return KLBannerStates.ELIMINATED;
    case KLUserStatuses.OFF:
      return KLBannerStates.START;
    default:
      return KLBannerStates.START;
  }
};

const hasNotification = (notifications, type, data = {}) => {
  return !!notifications?.find((n) => {
    if (n.type === type) {
      if ((!n.data && !data) || Object.keys(data).every((e) => n.data[e] === data[e])) {
        return true;
      }
    }
    return false;
  });
};

export const selectBannerState = createSelector(
  [selectUserConnected, selectKLUserStatus, selectKLUserRound, selectKLUserNotifications, selectKLGlobalStatus],
  (isUserConnected, userStatus, userRound, userNotifications, globalStatus) => {
    if (globalStatus === KLGlobalStatuses.ENDING) {
      return KLBannerStates.ENDING;
    }

    if (isUserConnected) {
      // Si le joueur a des notifications, on essaie de ne pas lui spoiler son avancée dans l'aventure
      // Donc on va chercher la bannière qui équivaut à son état avant les notifications recues
      let bannerState = getBannerStateForStatus(userStatus, userRound);

      // Si pas de notif, on ne modifie pas la bannière
      if (!userNotifications?.length) {
        return bannerState;
      } else {
        // Si c'est une bannière ELIMINATED
        // On met dans tous les cas la bannière du round en cours
        if (bannerState === KLBannerStates.ELIMINATED) {
          return getBannerStateForRound(userRound);
        }

        // Si c'est une bannière poteaux
        if (bannerState === KLBannerStates.POLE) {
          // On regarde si il y a un round complete orientation
          const found = hasNotification(userNotifications, KLNotificationTypes.ROUND_COMPLETE, {
            roundId: KLRounds.ORIENTATION,
          });

          if (found) {
            bannerState = KLBannerStates.ORIENTATION;
          }
        }

        // Si c'est une bannière orientation
        if (bannerState === KLBannerStates.ORIENTATION) {
          // On regarde si il y a un round complete tribu blanche
          const found = hasNotification(userNotifications, KLNotificationTypes.ROUND_COMPLETE, {
            roundId: KLRounds.T_WHITE,
          });

          if (found) {
            bannerState = KLBannerStates.T_WHITE;
          }
        }

        // Si c'est une bannière tribu blanche
        if (bannerState === KLBannerStates.T_WHITE) {
          // On regarde si il y a un round complete tribu rouge
          const found = hasNotification(userNotifications, KLNotificationTypes.ROUND_COMPLETE, {
            roundId: KLRounds.T_RED,
          });

          if (found) {
            bannerState = KLBannerStates.T_RED;
          }
        }

        return bannerState;
      }
    }

    return KLBannerStates.TEASER;
  },
);

const nbMsInADay = 1000 * 60 * 60 * 24;

const selectShouldBannerBeAnimated = createSelector(
  [selectKLUserLastMoveTs, selectUserConnected, selectKLUserStatus, selectUserDidReceivedUserData],
  (userLastMoveTs, isUserConnected, userStatus, didReceivedUserData) =>
    didReceivedUserData &&
    isUserConnected &&
    (userStatus === KLUserStatuses.OFF ||
      (typeof userLastMoveTs === 'number' && Date.now() - userLastMoveTs > nbMsInADay * 2)),
);

KLBannerSelectors.selectBackgroundPath = createSelector(
  [selectBannerState, selectShouldBannerBeAnimated],
  (bannerState, shouldBannerBeAnimated) => {
    if (shouldBannerBeAnimated) {
      return KLBannerAnimatedBackgroundPathFallbackByBannerStates[bannerState];
    }

    return KLBannerStaticBackgroundPathByBannerStates[bannerState];
  },
);

KLBannerSelectors.selectAnimatedBackgroundPath = createSelector(
  [selectBannerState, selectShouldBannerBeAnimated],
  (bannerState, shouldBannerBeAnimated) => {
    if (shouldBannerBeAnimated) {
      return KLBannerAnimatedBackgroundPathByBannerStates[bannerState];
    }
  },
);

KLBannerSelectors.selectButtonLabel = createSelector(
  [selectBannerState, selectUserDidReceivedUserData],
  (bannerState, didReceivedUserData) => {
    if (didReceivedUserData) {
      return KLBannersInfo[bannerState]?.buttonLabel;
    }
  },
);

KLBannerSelectors.selectText = createSelector(
  [selectBannerState, selectUserDidReceivedUserData],
  (bannerState, didReceivedUserData) => {
    if (didReceivedUserData) {
      return KLBannersInfo[bannerState]?.text;
    }
  },
);

export default KLBannerSelectors;
